import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, AlertTriangle, Clock, Bell, ChevronRight, X, Check, Edit3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DailyBriefingBar = ({ onExpandTasks }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const [deadlinesRes, transactionsRes, tasksRes, contentRes] = await Promise.all([
        axios.get(`${API_URL}/api/deadlines`, { withCredentials: true }),
        axios.get(`${API_URL}/api/transactions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tasks`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/content-calendar`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const chips = [];

      // Deadline alerts
      const deadlines = deadlinesRes.data.filter(d => d.status !== 'completed');
      const todayDeadlines = deadlines.filter(d => new Date(d.date).toDateString() === today.toDateString());
      const weekDeadlines = deadlines.filter(d => {
        const diff = Math.ceil((new Date(d.date) - today) / (1000 * 60 * 60 * 24));
        return diff >= 0 && diff <= 7;
      });
      if (todayDeadlines.length > 0) chips.push({ label: `${todayDeadlines.length} deadline${todayDeadlines.length > 1 ? 's' : ''} today`, color: '#B8860B', type: 'critical', items: todayDeadlines, itemType: 'deadline' });
      if (weekDeadlines.length > todayDeadlines.length) chips.push({ label: `${weekDeadlines.length} deadlines this week`, color: '#C2410C', type: 'warning', items: weekDeadlines, itemType: 'deadline' });

      // Bill alerts
      const dueBills = transactionsRes.data.filter(t => t.direction === 'out' && t.status === 'pending' && new Date(t.due_date) <= today);
      if (dueBills.length > 0) chips.push({ label: `${dueBills.length} bill${dueBills.length > 1 ? 's' : ''} overdue`, color: '#B8860B', type: 'critical', items: dueBills, itemType: 'bill' });

      // Task alerts (3-day and 7-day)
      const openTasks = tasksRes.data.filter(t => t.status !== 'done');
      const overdueTasks = openTasks.filter(t => t.due_date && new Date(t.due_date) < today);
      const tasks3day = openTasks.filter(t => {
        if (!t.due_date) return false;
        const diff = Math.ceil((new Date(t.due_date) - today) / (1000 * 60 * 60 * 24));
        return diff >= 0 && diff <= 3;
      });
      const tasks7day = openTasks.filter(t => {
        if (!t.due_date) return false;
        const diff = Math.ceil((new Date(t.due_date) - today) / (1000 * 60 * 60 * 24));
        return diff > 3 && diff <= 7;
      });

      if (overdueTasks.length > 0) chips.push({ label: `${overdueTasks.length} task${overdueTasks.length > 1 ? 's' : ''} overdue`, color: '#B8860B', type: 'critical', items: overdueTasks, itemType: 'task' });
      if (tasks3day.length > 0) chips.push({ label: `${tasks3day.length} task${tasks3day.length > 1 ? 's' : ''} due in 3 days`, color: '#EA580C', type: 'warning', items: tasks3day, itemType: 'task' });
      if (tasks7day.length > 0) chips.push({ label: `${tasks7day.length} task${tasks7day.length > 1 ? 's' : ''} due in 7 days`, color: '#D4AF37', type: 'info', items: tasks7day, itemType: 'task' });

      // Content calendar alerts
      const pendingContent = contentRes.data.filter(c => c.status !== 'published');
      const overdueContent = pendingContent.filter(c => c.scheduled_date && new Date(c.scheduled_date) < today);
      const content3day = pendingContent.filter(c => {
        if (!c.scheduled_date) return false;
        const diff = Math.ceil((new Date(c.scheduled_date) - today) / (1000 * 60 * 60 * 24));
        return diff >= 0 && diff <= 3;
      });
      const content7day = pendingContent.filter(c => {
        if (!c.scheduled_date) return false;
        const diff = Math.ceil((new Date(c.scheduled_date) - today) / (1000 * 60 * 60 * 24));
        return diff > 3 && diff <= 7;
      });

      if (overdueContent.length > 0) chips.push({ label: `${overdueContent.length} content overdue`, color: '#B8860B', type: 'critical', items: overdueContent, itemType: 'content' });
      if (content3day.length > 0) chips.push({ label: `${content3day.length} content due in 3 days`, color: '#EA580C', type: 'warning', items: content3day, itemType: 'content' });
      if (content7day.length > 0) chips.push({ label: `${content7day.length} content due in 7 days`, color: '#D4AF37', type: 'info', items: content7day, itemType: 'content' });

      const priority = { critical: 0, warning: 1, info: 2 };
      chips.sort((a, b) => priority[a.type] - priority[b.type]);

      // Daily school run reminder (Mon-Fri)
      const dayOfWeek = today.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const pickupTime = dayOfWeek === 5 ? '16:20' : '15:00';
        const pickupLabel = dayOfWeek === 5 ? 'School pick-up 4:20 PM (after club)' : 'School pick-up 3:00 PM';
        const now = new Date();
        const pickupHour = parseInt(pickupTime.split(':')[0]);
        const pickupMin = parseInt(pickupTime.split(':')[1]);
        const isBeforePickup = now.getHours() < pickupHour || (now.getHours() === pickupHour && now.getMinutes() < pickupMin);
        if (isBeforePickup) {
          chips.push({ label: pickupLabel, color: '#8B7355', type: 'info', items: [], itemType: 'reminder' });
        }
        // Morning drop-off reminder
        if (now.getHours() < 9) {
          chips.unshift({ label: 'School drop-off 8:30 AM', color: '#8B7355', type: 'info', items: [], itemType: 'reminder' });
        }
      }

      setAlerts(chips);
    } catch {
      // Silent fail
    }
  };

  const handleChipClick = (alert, index) => {
    setActivePanel(activePanel === index ? null : index);
  };

  const handleMarkTaskDone = async (task) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${task.id}`, { status: 'done' }, { withCredentials: true });
      fetchAlerts();
    } catch (err) {
      console.error('Failed to mark done:', err);
    }
  };

  const today = new Date();
  const hour = today.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const DAY_THEMES = {
    0: 'Church & Rest', 1: 'Finance & Admin', 2: 'Deep Work',
    3: 'Deep Work', 4: 'Sales & Meetings', 5: 'Content & Wrap-Up', 6: 'Family & Home'
  };
  const todayTheme = DAY_THEMES[today.getDay()];

  const visibleAlerts = expanded ? alerts : alerts.slice(0, 3);
  const activeAlertData = activePanel !== null ? alerts[activePanel] : null;

  return (
    <>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="daily-briefing-bar">
        <span className="text-[10px] font-medium" style={{ color: '#2C1A0E' }}>{greeting}</span>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: '#8B7355' }}>
          {today.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
        </span>
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#D4AF37', color: 'white' }}>
          {todayTheme}
        </span>
        {alerts.length === 0 ? (
          <span className="text-[10px]" style={{ color: '#8B7355' }}>All clear</span>
        ) : (
          <>
            {visibleAlerts.map((alert, i) => (
              <button
                key={i}
                onClick={() => handleChipClick(alert, i)}
                className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full transition-all cursor-pointer hover:scale-105"
                style={{
                  backgroundColor: activePanel === i ? alert.color : alert.color + '15',
                  color: activePanel === i ? 'white' : '#3D3526',
                  border: `1px solid ${alert.color}30`,
                }}
                data-testid={`alert-chip-${i}`}
              >
                {alert.type === 'critical' ? <AlertTriangle className="w-2.5 h-2.5" /> : alert.type === 'warning' ? <Bell className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
                {alert.label}
              </button>
            ))}
            {alerts.length > 3 && (
              <button onClick={() => setExpanded(!expanded)} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ color: '#5C4A2F' }} data-testid="expand-alerts">
                {expanded ? 'Less' : `+${alerts.length - 3}`}
              </button>
            )}
          </>
        )}
        <button onClick={() => navigate('/weekly-briefing')} className="flex items-center gap-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ color: '#5C4A2F', border: '1px solid #5C4A2F30' }} data-testid="briefing-bar-weekly-link">
          Full Briefing <ChevronRight className="w-2.5 h-2.5" />
        </button>
      </div>

      {/* Expandable Items Panel */}
      {activeAlertData && activeAlertData.items?.length > 0 && (
        <div
          className="border-t"
          style={{ backgroundColor: '#F0EDE6', borderColor: '#E8E3DC' }}
          data-testid="alert-items-panel"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest font-medium" style={{ color: activeAlertData.color }}>
                {activeAlertData.label}
              </span>
              <button
                onClick={() => setActivePanel(null)}
                className="p-1 rounded-full hover:bg-black/5 transition-all"
                data-testid="close-alert-panel"
              >
                <X className="w-3.5 h-3.5" style={{ color: '#A0937D' }} />
              </button>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {activeAlertData.items.map((item, idx) => {
                const dateField = item.due_date || item.scheduled_date || item.date || '';
                const dateStr = dateField ? new Date(dateField).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                const isOverdue = dateField && new Date(dateField) < new Date();
                const companyName = item.company_name || '';

                return (
                  <div
                    key={item.id || idx}
                    className="flex items-center gap-3 py-1.5 px-2 rounded transition-all hover:bg-black/5 group"
                    data-testid={`alert-item-${item.id || idx}`}
                  >
                    {activeAlertData.itemType === 'task' && (
                      <button
                        onClick={() => handleMarkTaskDone(item)}
                        className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all hover:bg-green-100 hover:border-green-500"
                        style={{ borderColor: '#C4B99A' }}
                        title="Mark as done"
                        data-testid={`alert-task-done-${item.id}`}
                      >
                        <Check className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" style={{ color: '#16A34A' }} />
                      </button>
                    )}
                    {activeAlertData.itemType !== 'task' && (
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: activeAlertData.color }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-xs truncate block" style={{ color: '#3D3526' }}>{item.title}</span>
                    </div>
                    {companyName && (
                      <span className="text-xs flex-shrink-0 px-1.5 py-0.5 rounded" style={{ color: '#8B7355', backgroundColor: '#E8E3DC' }}>
                        {companyName}
                      </span>
                    )}
                    {dateStr && (
                      <span className="text-xs flex-shrink-0 font-mono" style={{ color: isOverdue ? '#B8860B' : '#8B7355' }}>
                        {dateStr}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DailyBriefingBar;
